"use client"

import { Autocomplete as AutocompletePrimitive } from "@base-ui/react"
import { cn } from "cn"

import IconCross from "@/components/icons/cross"
import { FormControl } from "@/components/ui/form"
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupSize,
} from "@/components/ui/input-group"

const Autocomplete = AutocompletePrimitive.Root

function AutocompleteClear({
  className,
  ...props
}: AutocompletePrimitive.Clear.Props) {
  return (
    <AutocompletePrimitive.Clear
      data-slot="autocomplete-clear"
      aria-label="Clear"
      className={cn(
        "group/clear flex size-(--button-xs-size) items-center justify-center",
        className
      )}
      {...props}
    >
      <span className="flex size-4.5 items-center justify-center rounded-full text-2xs text-gray-8 transition-colors group-hover/clear:bg-gray-7 group-hover/clear:text-gray-1">
        <IconCross strokeWidth={2.5} />
      </span>
    </AutocompletePrimitive.Clear>
  )
}

function AutocompleteInput({
  className,
  children,
  disabled = false,
  size = "md",
  muted,
  useFormControl,
  ...props
}: Omit<AutocompletePrimitive.Input.Props, "size"> & {
  size?: InputGroupSize
  muted?: boolean
  useFormControl?: boolean
}) {
  const input = useFormControl ? (
    <FormControl render={<InputGroupInput disabled={disabled} />} />
  ) : (
    <InputGroupInput disabled={disabled} />
  )

  return (
    <AutocompletePrimitive.InputGroup
      render={
        <InputGroup
          className={cn("w-auto", className)}
          size={size}
          muted={muted}
        />
      }
    >
      <AutocompletePrimitive.Input render={input} {...props} />
      <InputGroupAddon
        align="inline-end"
        data-slot="input-group-clear"
        className="hidden has-[button]:flex"
      >
        <AutocompleteClear disabled={disabled} />
      </InputGroupAddon>
      {children}
    </AutocompletePrimitive.InputGroup>
  )
}

function AutocompleteContent({
  className,
  side = "bottom",
  sideOffset = 6,
  align = "start",
  alignOffset = 0,
  anchor,
  size = "anchor",
  ...props
}: AutocompletePrimitive.Popup.Props &
  Pick<
    AutocompletePrimitive.Positioner.Props,
    "side" | "align" | "sideOffset" | "alignOffset" | "anchor"
  > & {
    size?: "anchor" | "auto" | "sm" | "md" | "lg"
  }) {
  return (
    <AutocompletePrimitive.Portal>
      <AutocompletePrimitive.Positioner
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        anchor={anchor}
        className="isolate z-overlay"
      >
        <AutocompletePrimitive.Popup
          data-slot="autocomplete-content"
          className={cn(
            "group/autocomplete-content relative max-h-(--available-height) max-w-(--available-width) origin-(--transform-origin) overflow-hidden rounded-xl bg-surface-2 leading-tight shadow-ring-xl duration-100 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=inline-end]:slide-in-from-left-2 data-[side=inline-start]:slide-in-from-right-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 *:data-[slot=input-group]:m-1",
            size === "anchor" && "w-(--anchor-width) min-w-(--anchor-width)",
            size === "auto" && "w-fit min-w-(--anchor-width)",
            size === "sm" && "w-popover-sm",
            size === "md" && "w-popover-md",
            size === "lg" && "w-popover-lg",
            className
          )}
          {...props}
        />
      </AutocompletePrimitive.Positioner>
    </AutocompletePrimitive.Portal>
  )
}

function AutocompleteList({
  className,
  ...props
}: AutocompletePrimitive.List.Props) {
  return (
    <AutocompletePrimitive.List
      data-slot="autocomplete-list"
      className={cn(
        "scrollbar-hidden max-h-[min(calc(--spacing(72)-(--spacing(9))),calc(var(--available-height)-(--spacing(9))))] scroll-py-2.5 overflow-y-auto overscroll-contain px-2 py-2.5 data-empty:p-0",
        className
      )}
      {...props}
    />
  )
}

function AutocompleteItem({
  className,
  children,
  ...props
}: AutocompletePrimitive.Item.Props) {
  return (
    <AutocompletePrimitive.Item
      data-slot="autocomplete-item"
      className={cn(
        "relative flex min-h-8 w-full cursor-default items-center gap-2 rounded-lg px-2 py-[.442em] outline-hidden select-none data-disabled:pointer-events-none data-disabled:opacity-50 data-highlighted:bg-foreground/5",
        className
      )}
      {...props}
    >
      {children}
    </AutocompletePrimitive.Item>
  )
}

function AutocompleteGroup(props: AutocompletePrimitive.Group.Props) {
  return (
    <AutocompletePrimitive.Group data-slot="autocomplete-group" {...props} />
  )
}

function AutocompleteLabel(props: AutocompletePrimitive.GroupLabel.Props) {
  return (
    <AutocompletePrimitive.GroupLabel
      data-slot="autocomplete-label"
      {...props}
    />
  )
}

function AutocompleteCollection({
  ...props
}: AutocompletePrimitive.Collection.Props) {
  return (
    <AutocompletePrimitive.Collection
      data-slot="autocomplete-collection"
      {...props}
    />
  )
}

function AutocompleteEmpty({
  className,
  ...props
}: AutocompletePrimitive.Empty.Props) {
  return (
    <AutocompletePrimitive.Empty
      data-slot="autocomplete-empty"
      className={cn(
        "hidden w-full justify-center py-2 text-center text-sm text-muted group-data-empty/autocomplete-content:flex",
        className
      )}
      {...props}
    />
  )
}

function AutocompleteStatus(props: AutocompletePrimitive.Status.Props) {
  return (
    <AutocompletePrimitive.Status data-slot="autocomplete-status" {...props} />
  )
}

function AutocompleteSeparator({
  className,
  ...props
}: AutocompletePrimitive.Separator.Props) {
  return (
    <AutocompletePrimitive.Separator
      data-slot="autocomplete-separator"
      className={cn("-mx-1 my-1 h-px bg-separator", className)}
      {...props}
    />
  )
}

export {
  Autocomplete,
  AutocompleteInput,
  AutocompleteContent,
  AutocompleteList,
  AutocompleteItem,
  AutocompleteGroup,
  AutocompleteLabel,
  AutocompleteCollection,
  AutocompleteEmpty,
  AutocompleteStatus,
  AutocompleteSeparator,
  AutocompleteClear,
}
